import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { makeBackendApi } from 'services/BackendApi';
import {
  EConversionStrategy,
  EExchangeRateSource,
  IConversionRateFilters,
  ISystemExchangeRateListResponse,
  ISystemExchangeRatePayload,
  ISystemExchangeRateResponse,
} from '../types';
import { TCurrencyCode } from 'interfaces';
import { ESortOrder } from 'store/common/types';

export const CONVERSION_RATES_QUERY_KEY = 'conversionRates';

export interface ICreateConversionPairInput {
  fromCurrency: TCurrencyCode;
  toCurrency: TCurrencyCode;
  buyStrategy: EConversionStrategy;
  buyValue?: number | null;
  sellStrategy: EConversionStrategy;
  sellValue?: number | null;
}

const mapStrategyToSource = (strategy: EConversionStrategy): EExchangeRateSource => {
  if (strategy === 'fixed_rate') return 'fixed';
  return 'api';
};

const transformToBackendPayload = (input: ICreateConversionPairInput): ISystemExchangeRatePayload => {
  const payload: ISystemExchangeRatePayload = {
    from: input.fromCurrency,
    to: input.toCurrency,
    buySource: mapStrategyToSource(input.buyStrategy),
    sellSource: mapStrategyToSource(input.sellStrategy),
  };

  if (input.buyStrategy === 'fixed_rate' && input.buyValue != null) {
    payload.buy = input.buyValue;
  } else if (input.buyStrategy === 'markup_percentage' && input.buyValue != null) {
    payload.buyMarkupPercentage = input.buyValue;
  }

  if (input.sellStrategy === 'fixed_rate' && input.sellValue != null) {
    payload.sell = input.sellValue;
  } else if (input.sellStrategy === 'markup_percentage' && input.sellValue != null) {
    payload.sellMarkupPercentage = input.sellValue;
  }

  return payload;
};

export const useConversionRatesListQuery = (
  page: number,
  perPage: number,
  sortBy: string,
  sortOrder: ESortOrder,
  filters: IConversionRateFilters
) => {
  return useQuery<ISystemExchangeRateListResponse>({
    queryKey: [
      CONVERSION_RATES_QUERY_KEY,
      page, perPage, sortBy, sortOrder,
      filters.from, filters.to, filters.date,
    ],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const api = makeBackendApi();
      const response = await api.getSystemExchangeRates({
        page,
        limit: perPage,
        sortBy,
        sortDir: sortOrder,
        from: filters.from || undefined,
        to: filters.to || undefined,
        date: filters.date || undefined,
      });
      return response.data;
    },
  });
};

export const useCreateConversionPairMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ICreateConversionPairInput): Promise<ISystemExchangeRateResponse> => {
      const api = makeBackendApi();
      const payload = transformToBackendPayload(input);
      const response = await api.createSystemExchangeRate(payload);
      return response.data as ISystemExchangeRateResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONVERSION_RATES_QUERY_KEY] });
    },
  });
};

export const useUpdateConversionPairMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ICreateConversionPairInput): Promise<ISystemExchangeRateResponse> => {
      const api = makeBackendApi();
      const payload = transformToBackendPayload(input);
      const response = await api.updateSystemExchangeRate(payload);
      return response.data as ISystemExchangeRateResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONVERSION_RATES_QUERY_KEY] });
    },
  });
};
